import {FC, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {POST} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'

export enum TypeEntertainment {
  ENTERTAINMENT_EXPERIENCE = 'entertainment_experience',
  ENTERTAINMENT_ACTIVITY = 'entertainment_activity',
  ENTERTAINMENT_TOUR = 'entertainment_tour',
}

const getTypeLabel = (type: TypeEntertainment): string => {
  const labels = {
    [TypeEntertainment.ENTERTAINMENT_EXPERIENCE]: i18n.t('entertainment_services.type_experience'),
    [TypeEntertainment.ENTERTAINMENT_ACTIVITY]: i18n.t('entertainment_services.type_activity'),
    [TypeEntertainment.ENTERTAINMENT_TOUR]: i18n.t('entertainment_services.type_tour'),
  }
  return labels[type] || type
}

const CreateEntertainmentServiceContent: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    type: TypeEntertainment.ENTERTAINMENT_EXPERIENCE,
    status: true,
  })

  const handleInputChange = (field: string, value: string | TypeEntertainment | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('entertainment_services.msg_error_create')),
        text: String(i18n.t('entertainment_services.msg_error_required_title')),
      })
      return false
    }

    if (formData.title.length > 255) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('entertainment_services.msg_error_create')),
        text: String(i18n.t('entertainment_services.msg_error_title_length')),
      })
      return false
    }

    return true
  }

  const createService = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      const payload = {
        title: formData.title.trim(),
        type: formData.type,
        status: formData.status,
      }

      const response = await POST('/entertainment-service', payload)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('entertainment_services.btn_create')),
          text: String(i18n.t('entertainment_services.msg_success_create')),
        }).then(() => {
          navigate('/management/entertainment')
        })
      } else {
        throw new Error(response.message || i18n.t('entertainment_services.msg_error_create'))
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('entertainment_services.msg_error_create')),
        text: String(e.message || i18n.t('entertainment_services.msg_error_create')),
      })
      console.log(e)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='d-flex justify-content-between flex-wrap'>
            <div style={{flex: 1}}>
              <div className='w-100 card'>
                <div className='card-header py-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>
                      {i18n.t('entertainment_services.create')}
                    </span>
                    <BreadCrumbs />
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      {i18n.t('entertainment_services.create_subtitle')}
                    </span>
                  </h3>
                </div>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-lg-8'>
                      <Input
                        label={i18n.t('entertainment_services.field_title')}
                        inputAttr={{
                          type: 'text',
                          placeholder: i18n.t('entertainment_services.field_title_placeholder'),
                          maxLength: 255,
                        }}
                        name='title'
                        value={formData.title}
                        change={(value: string) => handleInputChange('title', value)}
                        maxCaracteres={255}
                      />
                    </div>
                    <div className='col-lg-4'>
                      <div className='mb-3 mt-6'>
                        <label className='form-label'>
                          {i18n.t('entertainment_services.field_type')}
                        </label>
                        <select
                          className='form-select'
                          value={formData.type}
                          onChange={(e) =>
                            handleInputChange('type', e.target.value as TypeEntertainment)
                          }
                        >
                          {Object.values(TypeEntertainment).map((type) => (
                            <option key={type} value={type}>
                              {getTypeLabel(type)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-lg-12'>
                      <Input
                        inputAttr={{
                          type: 'checkbox',
                        }}
                        label={i18n.t('entertainment_services.field_status')}
                        name='status'
                        checked={formData.status}
                        change={() => handleInputChange('status', !formData.status)}
                      />
                    </div>
                  </div>

                  <div className='d-flex justify-content-end mt-5'>
                    <Button
                      text={i18n.t('buttons.back')}
                      color='info'
                      size='small'
                      click={() => navigate('/management/entertainment')}
                      btnClass='me-3'
                    />
                    <Button
                      size='small'
                      text={i18n.t('buttons.save')}
                      color='primary'
                      click={createService}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const CreateEntertainmentService: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('general.dashboard'), path: 'dashboard', isActive: true},
          {
            title: i18n.t('entertainment_services.list_title'),
            path: 'management/entertainment',
            isActive: true,
          },
          {
            title: i18n.t('entertainment_services.create_title'),
            path: 'management/entertainment/create',
            isActive: true,
          },
        ]}
      >
        {i18n.t('entertainment_services.create_title')}
      </PageTitle>
      <CreateEntertainmentServiceContent />
    </>
  )
}

export {CreateEntertainmentService}
